import React, { useEffect, useMemo, useState } from 'react';

type ActionGroupItem = {
  actionCall: {
    id: string;
    method: string;
    params: Record<string, unknown>;
    role?: string;
    flowId?: string;
  };
  actionResult?: {
    id: string;
    method: string;
    result?: unknown;
    error?: { code: number; message: string };
  } | null;
};

const FLOW_LABELS: Record<string, string> = {
  'general-assistant': 'General Assistant',
  'clinic-assistant': 'Clinic Assistant',
};

const METHOD_ICONS: Record<string, string> = {
  'files.read': '📄',
  'files.write': '✏️',
  'files.edit': '✏️',
  'files.glob': '🔍',
  'files.grep': '🔍',
  'medical.vitals_query': '❤️',
  'medical.alert_trigger': '🚨',
  'medical.record_query': '📋',
  'gmail.send': '📧',
  'gmail.read': '📨',
  'gmail.template': '📝',
  'scheduling.followup_create': '📅',
  'scheduling.calendar_check': '🗓️',
  'logging.create': '📝',
  'logging.search': '🔍',
  'logging.update': '✏️',
  'clinic_sync.clinical_logs': '🔄',
  'shell.exec': '💻',
  'memory.search': '🧠',
  'memory.list': '🧠',
  'memory.create': '🧠',
  'memory.update': '🧠',
  'memory.delete': '🧠',
  'memory.working.push': '📝',
  'memory.working.set': '📝',
  'memory.working.clear': '📝',
};

const getMethodIcon = (method: string): string => METHOD_ICONS[method] || '⚡';

const getMethodLabel = (method: string): string => {
  const parts = method.split('.');
  if (parts.length === 2) return `${parts[0]}/${parts[1]}`;
  return method;
};

const formatActionParams = (method: string, params: Record<string, unknown>): string => {
  const summaryKeys: Record<string, string[]> = {
    'files.read': ['filePath'],
    'files.write': ['filePath'],
    'files.edit': ['filePath'],
    'files.glob': ['pattern'],
    'files.grep': ['pattern'],
    'medical.vitals_query': ['subjectId', 'metric'],
    'gmail.send': ['to', 'subject'],
    'gmail.read': ['query'],
    'scheduling.followup_create': ['subjectId'],
    'logging.create': ['title', 'category', 'status'],
    'logging.search': ['category', 'status', 'subjectId', 'limit'],
    'logging.update': ['logId', 'title', 'status', 'priority'],
    'clinic_sync.clinical_logs': ['patientId', 'search', 'limit'],
    'shell.exec': ['command'],
    'memory.search': ['query'],
    'memory.create': ['text'],
    'memory.update': ['id'],
    'memory.delete': ['id'],
    'memory.working.push': ['text'],
  };

  const keys = summaryKeys[method];
  if (!keys) return JSON.stringify(params, null, 2);

  const parts: string[] = [];
  for (const key of keys) {
    const val = params[key];
    if (val !== undefined) {
      parts.push(`${key}=${typeof val === 'string' ? val : JSON.stringify(val)}`);
    }
  }
  return parts.join(', ') || JSON.stringify(params);
};

const useProgressiveReveal = (total: number, triggerKey: string | null): number => {
  const [visibleCount, setVisibleCount] = useState(triggerKey ? 0 : total);

  useEffect(() => {
    if (!triggerKey || total <= 0) {
      setVisibleCount(total);
      return undefined;
    }

    setVisibleCount(0);
    const timer = window.setInterval(() => {
      setVisibleCount((current) => {
        if (current >= total) {
          window.clearInterval(timer);
          return current;
        }
        return current + 1;
      });
    }, 90);

    return () => window.clearInterval(timer);
  }, [total, triggerKey]);

  return Math.min(visibleCount, total);
};

const getProgressiveTotal = (method: string, result: unknown): number => {
  if (!result || typeof result !== 'object') return 0;
  if (method === 'clinic_sync.clinical_logs') return 10;
  if (method === 'logging.create' || method === 'logging.update') return 4;
  if (method === 'logging.search') {
    const logs = (result as { logs?: unknown }).logs;
    if (!Array.isArray(logs)) return 0;
    return logs.filter((log) => (
      log
      && typeof log === 'object'
      && (log as { category?: unknown }).category === 'clinic_clinical_log'
    )).length;
  }
  return 0;
};

const renderClinicSyncResult = (result: unknown, visibleCount = Number.POSITIVE_INFINITY): React.ReactNode => {
  if (!result || typeof result !== 'object') return null;
  const payload = result as {
    tool?: unknown;
    status?: unknown;
    patientsRead?: unknown;
    patientsImported?: unknown;
    patientsUpdated?: unknown;
    patientsSkipped?: unknown;
    patientsFailed?: unknown;
    read?: unknown;
    imported?: unknown;
    refreshed?: unknown;
    skipped?: unknown;
    failed?: unknown;
    filters?: { patientId?: unknown; search?: unknown; limit?: unknown };
  };
  if (payload.tool !== 'clinic_sync_clinical_logs') return null;
  const filters = payload.filters;

  const patientTiles: Array<[string, unknown]> = [
    ['Read', payload.patientsRead],
    ['New', payload.patientsImported],
    ['Updated', payload.patientsUpdated],
    ['Skipped', payload.patientsSkipped],
    ['Failed', payload.patientsFailed],
  ];
  const clinicalTiles: Array<[string, unknown]> = [
    ['Read', payload.read],
    ['New', payload.imported],
    ['Updated', payload.refreshed],
  ];
  if (payload.skipped !== undefined) clinicalTiles.push(['Skipped', payload.skipped]);
  clinicalTiles.push(['Failed', payload.failed]);
  const visiblePatientTiles = patientTiles.slice(0, visibleCount);
  const visibleClinicalTiles = clinicalTiles.slice(0, Math.max(0, visibleCount - patientTiles.length));

  return (
    <div className="mt-3 rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <div className="px-3 py-2 text-sm font-semibold dark:text-claude-darkText text-claude-text">
        Clinic Sync Result
      </div>
      <div className="px-3 pt-2 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
        Patients
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 pt-2">
        {visiblePatientTiles.map(([label, value]) => (
          <div key={`patient-${String(label)}`} className="rounded-lg dark:bg-claude-darkSurface bg-claude-surface p-2">
            <div className="text-[10px] uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {label}
            </div>
            <div className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
              {String(value ?? 0)}
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 pt-1 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
        Clinical Logs
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
        {visibleClinicalTiles.map(([label, value]) => (
          <div key={`log-${String(label)}`} className="rounded-lg dark:bg-claude-darkSurface bg-claude-surface p-2">
            <div className="text-[10px] uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {label}
            </div>
            <div className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
              {String(value ?? 0)}
            </div>
          </div>
        ))}
      </div>
      {Boolean(filters?.patientId || filters?.search) && (
        <div className="px-3 pb-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {filters?.patientId ? `Patient: ${String(filters.patientId)}` : ''}
          {filters?.patientId && filters?.search ? ' · ' : ''}
          {filters?.search ? `Search: ${String(filters.search)}` : ''}
        </div>
      )}
    </div>
  );
};

const renderLogCreateResult = (result: unknown, visibleCount = Number.POSITIVE_INFINITY): React.ReactNode => {
  if (!result || typeof result !== 'object') return null;
  const payload = result as {
    tool?: unknown;
    status?: unknown;
    logId?: unknown;
    params?: {
      title?: unknown;
      category?: unknown;
      priority?: unknown;
    };
  };
  if (payload.tool !== 'log_create') return null;

  const fields = ([
    ['Log ID', payload.logId],
    ['Category', payload.params?.category],
    ['Title', payload.params?.title],
    ['Priority', payload.params?.priority],
  ] as Array<[string, unknown]>).slice(0, visibleCount);

  return (
    <div className="mt-3 rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <div className="px-3 py-2 text-sm font-semibold dark:text-claude-darkText text-claude-text">
        Local Log Created Successfully
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
        {fields.map(([label, value]) => (
          <div key={label} className="rounded-lg dark:bg-claude-darkSurface bg-claude-surface p-2">
            <div className="text-[10px] uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {label}
            </div>
            <div className="text-sm font-medium dark:text-claude-darkText text-claude-text break-words">
              {String(value ?? '')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const renderLogUpdateResult = (result: unknown, visibleCount = Number.POSITIVE_INFINITY): React.ReactNode => {
  if (!result || typeof result !== 'object') return null;
  const payload = result as {
    tool?: unknown;
    status?: unknown;
    logId?: unknown;
    params?: {
      logId?: unknown;
      title?: unknown;
      status?: unknown;
      priority?: unknown;
    };
  };
  if (payload.tool !== 'log_update') return null;

  const fields = ([
    ['Log ID', payload.logId || payload.params?.logId],
    ['Title', payload.params?.title],
    ['Status', payload.params?.status],
    ['Priority', payload.params?.priority],
  ] as Array<[string, unknown]>).slice(0, visibleCount);

  return (
    <div className="mt-3 rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <div className="px-3 py-2 text-sm font-semibold dark:text-claude-darkText text-claude-text">
        Local Log Updated Successfully
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
        {fields.map(([label, value]) => (
          <div key={label} className="rounded-lg dark:bg-claude-darkSurface bg-claude-surface p-2">
            <div className="text-[10px] uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {label}
            </div>
            <div className="text-sm font-medium dark:text-claude-darkText text-claude-text break-words">
              {value === undefined ? '-' : String(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const formatActionResult = (result: unknown): string => {
  if (typeof result === 'string') return result;
  if (result === null || result === undefined) return '';
  if (Array.isArray(result)) {
    return JSON.stringify(result, null, 2);
  }
  if (typeof result === 'object') {
    return JSON.stringify(result, null, 2);
  }
  return String(result);
};

const getClinicField = (description: string, label: string): string => {
  const line = description.split('\n').find((item) => item.toLowerCase().startsWith(label.toLowerCase()));
  return line ? line.slice(label.length).trim() : '';
};

const renderClinicLogSearch = (result: unknown, visibleCount = Number.POSITIVE_INFINITY): React.ReactNode => {
  if (!result || typeof result !== 'object') return null;
  const logs = (result as { logs?: unknown }).logs;
  if (!Array.isArray(logs)) return null;
  const clinicLogs = logs.filter((log) => (
    log
    && typeof log === 'object'
    && (log as { category?: unknown }).category === 'clinic_clinical_log'
  )) as Array<{ id?: string; title?: string; description?: string; startTime?: number }>;
  if (clinicLogs.length === 0) return null;

  const rows = clinicLogs.map((log) => {
    const description = log.description || '';
    return {
      id: getClinicField(description, 'Clinic Log ID:') || log.id || '',
      patient: getClinicField(description, 'Patient ID:'),
      subjective: getClinicField(description, 'Subjective:'),
      assessment: getClinicField(description, 'Assessment:'),
      time: getClinicField(description, 'Time:') || (log.startTime ? new Date(log.startTime).toLocaleString() : ''),
    };
  });

  const visibleRows = rows.slice(0, visibleCount);

  return (
    <div className="mt-3 rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <div className="px-3 py-2 text-sm font-semibold dark:text-claude-darkText text-claude-text">
        Found {rows.length} clinical records
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="dark:bg-claude-darkBg bg-claude-bg">
            <tr className="dark:text-claude-darkTextSecondary text-claude-textSecondary">
              <th className="px-3 py-2 text-left font-medium">ID</th>
              <th className="px-3 py-2 text-left font-medium">Patient</th>
              <th className="px-3 py-2 text-left font-medium">Subjective</th>
              <th className="px-3 py-2 text-left font-medium">Assessment</th>
              <th className="px-3 py-2 text-left font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={`${row.id}-${index}`} className="border-t dark:border-claude-darkBorder border-claude-border">
                <td className="px-3 py-2 dark:text-claude-darkText text-claude-text">{row.id}</td>
                <td className="px-3 py-2 dark:text-claude-darkText text-claude-text">{row.patient}</td>
                <td className="px-3 py-2 dark:text-claude-darkText text-claude-text max-w-[260px] truncate">{row.subjective}</td>
                <td className="px-3 py-2 dark:text-claude-darkText text-claude-text max-w-[260px] truncate">{row.assessment}</td>
                <td className="px-3 py-2 dark:text-claude-darkText text-claude-text whitespace-nowrap">{row.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ActionGroup: React.FC<{
  group: ActionGroupItem;
  isLastInSequence?: boolean;
}> = ({
  group,
  isLastInSequence = true,
}) => {
  const { actionCall, actionResult } = group;
  const [isExpanded, setIsExpanded] = useState(false);
  const icon = getMethodIcon(actionCall.method);
  const label = getMethodLabel(actionCall.method);
  const paramSummary = formatActionParams(actionCall.method, actionCall.params);
  const isActionError = Boolean(actionResult?.error);
  const resultDisplay = actionResult ? formatActionResult(actionResult?.result) : '';
  const resultLineCount = resultDisplay ? resultDisplay.split('\n').length : 0;
  const flowLabel = actionCall.flowId ? (FLOW_LABELS[actionCall.flowId] || actionCall.flowId) : null;
  const progressiveTotal = useMemo(
    () => (actionResult && !isActionError ? getProgressiveTotal(actionCall.method, actionResult.result) : 0),
    [actionCall.method, actionResult, isActionError],
  );
  const progressiveTriggerKey = actionResult && !isActionError && progressiveTotal > 0
    ? `${actionCall.id}:${actionResult.id}:${actionCall.method}`
    : null;
  const visibleCount = useProgressiveReveal(progressiveTotal, progressiveTriggerKey);
  const isRenderingResult = Boolean(actionResult && !isActionError && progressiveTotal > visibleCount);
  const clinicLogSearch = actionResult && !isActionError && actionCall.method === 'logging.search'
    ? renderClinicLogSearch(actionResult.result, visibleCount)
    : null;
  const clinicSyncResult = actionResult && !isActionError && actionCall.method === 'clinic_sync.clinical_logs'
    ? renderClinicSyncResult(actionResult.result, visibleCount)
    : null;
  const logCreateResult = actionResult && !isActionError && actionCall.method === 'logging.create'
    ? renderLogCreateResult(actionResult.result, visibleCount)
    : null;
  const logUpdateResult = actionResult && !isActionError && actionCall.method === 'logging.update'
    ? renderLogUpdateResult(actionResult.result, visibleCount)
    : null;

  return (
    <div className="relative py-1">
      {!isLastInSequence && (
        <div className="absolute left-[3.5px] top-[14px] bottom-[-8px] w-px dark:bg-claude-darkTextSecondary/30 bg-claude-textSecondary/30" />
      )}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-2 text-left group relative z-10"
      >
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
          !actionResult
            ? 'bg-blue-500 animate-pulse'
            : isActionError
              ? 'bg-red-500'
              : 'bg-green-500'
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm">{icon}</span>
            <span className="text-sm font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {label}
            </span>
            {flowLabel && (
              <span className="text-[10px] px-1.5 py-0.5 rounded dark:bg-claude-accent/20 bg-claude-accent/10 dark:text-claude-accent text-claude-accent">
                {flowLabel}
              </span>
            )}
            {paramSummary && (
              <code className="text-xs dark:text-claude-darkTextSecondary/80 text-claude-textSecondary/80 font-mono truncate max-w-[400px]">
                {paramSummary}
              </code>
            )}
          </div>
          {actionResult && resultLineCount > 0 && (
            <div className="text-xs dark:text-claude-darkTextSecondary/60 text-claude-textSecondary/60 mt-0.5">
              {resultLineCount} {resultLineCount === 1 ? 'line' : 'lines'} of response
            </div>
          )}
          {!actionResult && (
            <div className="text-xs dark:text-claude-darkTextSecondary/60 text-claude-textSecondary/60 mt-0.5">
              Running...
            </div>
          )}
          {isRenderingResult && (
            <div className="text-xs dark:text-claude-darkTextSecondary/60 text-claude-textSecondary/60 mt-0.5">
              Rendering result...
            </div>
          )}
        </div>
      </button>
      {clinicLogSearch && (
        <div className="ml-4">
          {clinicLogSearch}
        </div>
      )}
      {clinicSyncResult && (
        <div className="ml-4">
          {clinicSyncResult}
        </div>
      )}
      {logCreateResult && (
        <div className="ml-4">
          {logCreateResult}
        </div>
      )}
      {logUpdateResult && (
        <div className="ml-4">
          {logUpdateResult}
        </div>
      )}
      {isExpanded && (
        <div className="ml-4 mt-2">
          <div className="space-y-2">
            <div>
              <div className="text-[10px] font-medium dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70 uppercase tracking-wider mb-1">
                Parameters
              </div>
              <div className="max-h-48 overflow-y-auto">
                <pre className="text-xs dark:text-claude-darkText text-claude-text whitespace-pre-wrap break-words font-mono">
                  {actionCall.method}({JSON.stringify(actionCall.params, null, 2)})
                </pre>
              </div>
            </div>
            {actionResult && (
              <div>
                <div className="text-[10px] font-medium dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70 uppercase tracking-wider mb-1">
                  {isActionError ? 'Error' : 'Result'}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <pre className={`text-xs whitespace-pre-wrap break-words font-mono ${
                    isActionError ? 'text-red-500' : 'dark:text-claude-darkText text-claude-text'
                  }`}>
                    {isActionError ? actionResult!.error!.message : resultDisplay}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionGroup;
export type { ActionGroupItem };
